'use client'

import { motion, useInView } from 'framer-motion'
import { useRef, useId } from 'react'
import { Container } from '@/components/ui/container'
import { Section } from '@/components/ui/section'
import { cn } from '@/lib/utils'
import { type ProductVariant, productStyles } from './product-styles'
import { sectionIconMap } from './icon-map'

const defaultChartData = [30, 40, 35, 50, 45, 60, 55, 75, 70, 100]

const chartColors: Record<ProductVariant, string> = {
  ai: 'var(--site-brand-violet)',
  bp: 'var(--site-brand-green)',
  marketplace: 'var(--site-brand-blue)',
}

interface Stat {
  value: string
  label: string
  description: string
  icon: string
  data?: number[]
  decreasing?: boolean
  highlight?: boolean
  wide?: boolean
}

interface ProductPerformanceStatsSectionProps {
  variant: ProductVariant
  eyebrow?: string
  title: string
  subtitle: string
  stats: Stat[]
}

function MiniChart({ data, color, decreasing }: { data: number[]; color: string; decreasing?: boolean }) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, amount: 0.5 })

  const height = 40
  const width = 120
  const max = Math.max(...data)
  const min = Math.min(...data)
  const range = max - min || 1

  const points = data
    .map((value, index) => {
      const x = (index / (data.length - 1)) * width
      const y = height - ((value - min) / range) * height
      return `${x},${y}`
    })
    .join(' ')

  const areaPath = `M 0,${height} L ${points} L ${width},${height} Z`
  const linePath = `M ${points.split(' ').join(' L ')}`
  const gradientId = `gradient-${useId().replace(/:/g, '')}`

  return (
    <svg ref={ref} width={width} height={height} className="overflow-visible">
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={color} stopOpacity="0.2" />
          <stop offset="100%" stopColor={color} stopOpacity="0.6" />
        </linearGradient>
      </defs>
      <motion.path
        d={areaPath}
        fill={`url(#${gradientId})`}
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
      />
      <motion.path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={isInView ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }}
        transition={{ duration: 1, ease: 'easeOut' }}
      />
      <motion.circle
        cx={width}
        cy={height - ((data[data.length - 1] - min) / range) * height}
        r="4"
        fill={color}
        initial={{ scale: 0, opacity: 0 }}
        animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
        transition={{ duration: 0.3, delay: 0.8 }}
      />
      <motion.text
        x={width + 8}
        y={height - ((data[data.length - 1] - min) / range) * height + 4}
        fill={color}
        fontSize="12"
        fontWeight="bold"
        initial={{ opacity: 0, y: 5 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 5 }}
        transition={{ duration: 0.3, delay: 0.9 }}
      >
        {decreasing ? '↓' : '↑'}
      </motion.text>
    </svg>
  )
}

function WideChart({ data, color, label, labelColor }: { data: number[]; color: string; label: string; labelColor: string }) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, amount: 0.5 })

  const height = 80
  const width = 200
  const max = Math.max(...data)
  const min = Math.min(...data)
  const range = max - min || 1

  const extendedData = [...data, ...data.slice(-3).map((v) => v * 1.05)]
  const points = extendedData
    .map((value, index) => {
      const x = (index / (extendedData.length - 1)) * width
      const y = height - ((value - min) / range) * height
      return `${x},${y}`
    })
    .join(' ')

  const areaPath = `M 0,${height} L ${points} L ${width},${height} Z`
  const linePath = `M ${points.split(' ').join(' L ')}`
  const endY = height - ((extendedData[extendedData.length - 1] - min) / range) * height
  const gradientId = `wide-gradient-${useId().replace(/:/g, '')}`

  return (
    <svg ref={ref} width={width} height={height + 10} className="overflow-visible">
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      {[0, 1, 2, 3].map((i) => (
        <line
          key={i}
          x1="0"
          y1={(height / 3) * i}
          x2={width}
          y2={(height / 3) * i}
          stroke={color}
          strokeOpacity="0.1"
          strokeWidth="1"
        />
      ))}
      <motion.path
        d={areaPath}
        fill={`url(#${gradientId})`}
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      />
      <motion.path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={isInView ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }}
        transition={{ duration: 1.2, ease: 'easeOut' }}
      />
      <motion.circle
        cx={width}
        cy={endY}
        r="6"
        fill={color}
        initial={{ scale: 0, opacity: 0 }}
        animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
        transition={{ duration: 0.3, delay: 1 }}
      />
      <motion.g
        initial={{ opacity: 0, y: 10 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
        transition={{ duration: 0.4, delay: 1.1 }}
      >
        <rect x={width - 60} y={endY - 28} width="50" height="20" rx="4" fill={color} />
        <text
          x={width - 35}
          y={endY - 14}
          fill={labelColor}
          fontSize="10"
          fontWeight="bold"
          textAnchor="middle"
        >
          {label}
        </text>
      </motion.g>
    </svg>
  )
}

export function ProductPerformanceStatsSection({
  variant,
  eyebrow,
  title,
  subtitle,
  stats,
}: ProductPerformanceStatsSectionProps) {
  const styles = productStyles[variant]
  const color = chartColors[variant]
  const labelColor = 'var(--site-page)'

  return (
    <Section background={styles.background}>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          {eyebrow && (
            <p className={`text-micro-uppercase ${styles.eyebrow} mb-3`}>{eyebrow}</p>
          )}
          <h2 className={`text-display-1 ${styles.title} mb-3`}>{title}</h2>
          <p className={`text-body-lg ${styles.subtitle} max-w-xl mx-auto`}>{subtitle}</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {stats.map((stat, index) => {
            const IconComponent = sectionIconMap[stat.icon]
            const data = stat.data || defaultChartData
            const extendedData = [...data, ...data.slice(-3).map((v) => v * 1.05)]
            const isWide = stat.wide

            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className={cn(isWide && 'md:col-span-2')}
              >
                <div
                  className={cn(
                    'group rounded-2xl p-6 border transition-all duration-200',
                    styles.card,
                    styles.cardBorder,
                    isWide && 'h-full'
                  )}
                >
                  {isWide ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-6 h-full">
                      <div className="flex-1 text-center md:text-left">
                        <div className={cn('inline-flex p-3 rounded-xl mb-4', styles.iconBg, styles.iconColor)}>
                          <IconComponent className="w-7 h-7" />
                        </div>
                        <div className={cn('text-5xl md:text-6xl font-bold mb-2', styles.iconColor)}>
                          {stat.value}
                        </div>
                        <div className={cn('text-lg font-semibold mb-2', styles.cardTitle)}>{stat.label}</div>
                        <p className={cn('text-sm', styles.cardText)}>{stat.description}</p>
                      </div>
                      <div className="flex-1 flex justify-center md:justify-end">
                        <WideChart data={extendedData} color={color} label={stat.value} labelColor={labelColor} />
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className={cn('inline-flex p-3 rounded-xl mb-4', styles.iconBg, styles.iconColor)}>
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <div className={cn('text-4xl md:text-5xl font-bold mb-2', styles.iconColor)}>
                        {stat.value}
                      </div>
                      <div className={cn('font-semibold mb-2', styles.cardTitle)}>{stat.label}</div>
                      <div className="flex justify-center mb-3">
                        <MiniChart data={data} color={color} decreasing={stat.decreasing} />
                      </div>
                      <p className={cn('text-sm', styles.cardText)}>{stat.description}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )
          })}
        </div>
      </Container>
    </Section>
  )
}
